import { useCallback, useEffect, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import { api, type AutokartonValasz, type KeszletNaploValasz } from '../api.ts';
import { szam } from '../formazas.ts';
import { letolt } from '../letoltes.ts';

/**
 * Autókészlet — a napló és az autókarton. (Megbízás 6.6)
 *
 * A mai Excel elrendezését követi: sorok a napok, autónként
 * `Nyitó | Fel | Le | Záró | Eltérés`. Az **eltérés** oszlop új: megszámolt
 * mínusz számított záró.
 *
 * A lánc a megszámolt zárón folytatódik, ezért egy eltérés nem gyűrűzik
 * tovább a következő napokra.
 */
export function Autokeszlet() {
  /** Letöltés csak engedéllyel. (3.0 / 1.) */
  const letolthet = useMuvelet('export_kimutatas');
  const [honapok, setHonapok] = useState<string[]>([]);
  const [honap, setHonap] = useState<string | null>(null);
  const [naplo, setNaplo] = useState<KeszletNaploValasz | null>(null);
  const [karton, setKarton] = useState<number | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [toltes, setToltes] = useState(true);

  useEffect(() => {
    api
      .get<{ honapok: string[] }>('/api/keszlet/honapok')
      .then((v) => {
        setHonapok(v.honapok);
        setHonap((h) => h ?? v.honapok[0] ?? null);
      })
      .catch((h: Error) => setHiba(h.message));
  }, []);

  const betolt = useCallback(async () => {
    if (!honap) return;
    setToltes(true);
    try {
      setNaplo(await api.get<KeszletNaploValasz>(`/api/keszlet/naplo/${honap}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a naplót.');
    } finally {
      setToltes(false);
    }
  }, [honap]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  if (karton !== null) {
    return <Autokarton sofor_id={karton} onVissza={() => setKarton(null)} />;
  }

  const autok = naplo?.autok ?? [];

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Autókészlet</span>
        <select value={honap ?? ''} onChange={(e) => setHonap(e.target.value)}>
          {honapok.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        {letolthet && (
          <button
            className="btn sm"
            disabled={!honap}
            onClick={() => honap && letolt(`/api/export/keszlet/${honap}`)}
            title="A havi készletnapló Excelben"
          >
            Excel
          </button>
        )}
        <span className="hint">
          Az olaj, az F+ és a raktári együtt, egyetlen literszámként — a kocsiban is
          egy tartály van. Az autó nevére kattintva megnyílik a kartonja.
        </span>
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      {autok.length === 0 && !toltes && (
        <div className="blank">
          Még nincs felvéve sofőr. A Törzsadatok → Sofőrök és autók fülön vedd fel őket.
        </div>
      )}

      {autok.length > 0 && (
        <div className="matrix">
          <table className="tt2 ksT">
            <thead>
              <tr>
                <th className="dcol" rowSpan={2}>
                  Nap
                </th>
                {autok.map((a) => (
                  <th
                    key={a.sofor_id}
                    className="carh"
                    colSpan={5}
                    onClick={() => setKarton(a.sofor_id)}
                    title="Kattints a karton megnyitásához"
                  >
                    {a.nev}
                    {a.rendszam ? ` · ${a.rendszam}` : ''}
                    {!a.aktiv && ' (nem aktív)'}
                  </th>
                ))}
              </tr>
              <tr>
                {autok.map((a) => (
                  <ReszFejlec key={a.sofor_id} />
                ))}
              </tr>
            </thead>
            <tbody>
              {(naplo?.sorok ?? []).map((s) => (
                <tr key={s.datum}>
                  <td className="dcol">
                    {s.felirat}
                    <small className="halvany"> · {s.statusz}</small>
                  </td>
                  {autok.map((a) => {
                    const c = s.cellak[a.sofor_id];
                    if (!c) {
                      return (
                        <td
                          key={a.sofor_id}
                          colSpan={5}
                          className="calc2"
                          style={{ color: 'var(--ghost)', textAlign: 'center' }}
                        >
                          —
                        </td>
                      );
                    }
                    return (
                      <Cellak
                        key={a.sofor_id}
                        nyito={c.nyito}
                        fel={c.fel}
                        le={c.le}
                        zaro={c.zaro}
                        elteres={c.elteres}
                        megszamolt={c.megszamolt_zaro !== null}
                      />
                    );
                  })}
                </tr>
              ))}
              {(naplo?.sorok.length ?? 0) === 0 && (
                <tr>
                  <td
                    colSpan={1 + autok.length * 5}
                    style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}
                  >
                    {toltes ? 'Betöltés…' : 'Ebben a hónapban még nincs elszámolt nap.'}
                  </td>
                </tr>
              )}
            </tbody>
            {(naplo?.sorok.length ?? 0) > 0 && (
              <tfoot>
                <tr>
                  <td className="dcol">Hónap végi záró</td>
                  {autok.map((a) => (
                    <td key={a.sofor_id} colSpan={5} style={{ textAlign: 'center' }}>
                      {szam(naplo!.zarok[a.sofor_id] ?? 0, 1)} l
                      {(naplo!.elteresek[a.sofor_id] ?? 0) !== 0 && (
                        <span className="bad2">
                          {' '}
                          · eltérés {elteresJel(naplo!.elteresek[a.sofor_id] ?? 0)}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </>
  );
}

function ReszFejlec() {
  return (
    <>
      <th className="sub n">Nyitó</th>
      <th className="sub n">Fel</th>
      <th className="sub n">Le</th>
      <th className="sub n">Záró</th>
      <th className="sub n">Eltérés</th>
    </>
  );
}

function Cellak({
  nyito,
  fel,
  le,
  zaro,
  elteres,
  megszamolt,
}: {
  nyito: number;
  fel: number;
  le: number;
  zaro: number;
  elteres: number | null;
  megszamolt: boolean;
}) {
  return (
    <>
      <td className="calc2">{szam(nyito, 1)}</td>
      <td className="calc2">{fel ? szam(fel, 1) : '·'}</td>
      <td className="calc2">{le ? szam(le, 1) : '·'}</td>
      <td className="calc2 zr" title={megszamolt ? 'megszámolt záró' : 'számított záró'}>
        <b>{szam(zaro, 1)}</b>
      </td>
      <td className="calc2">
        {elteres === null ? (
          <span className="halvany">—</span>
        ) : elteres === 0 ? (
          <span style={{ color: 'var(--ok)' }}>✓</span>
        ) : (
          <span className="bad2">{elteresJel(elteres)}</span>
        )}
      </td>
    </>
  );
}

function elteresJel(n: number): string {
  return `${n > 0 ? '+' : ''}${szam(n, 1)}`;
}

/* ------------------------------------------------------------------ */

/** Egy autó kartonja: minden mozgás időrendben, folyamatos egyenleggel. */
function Autokarton({ sofor_id, onVissza }: { sofor_id: number; onVissza: () => void }) {
  const [karton, setKarton] = useState<AutokartonValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<AutokartonValasz>(`/api/keszlet/karton/${sofor_id}`)
      .then(setKarton)
      .catch((h: Error) => setHiba(h.message));
  }, [sofor_id]);

  if (hiba) return <div className="uzenet bad">{hiba}</div>;
  if (!karton) return <div className="halvany" style={{ padding: 20 }}>Betöltés…</div>;

  return (
    <>
      <div className="dayhead">
        <button className="btn sm" onClick={onVissza}>
          ← vissza a naplóhoz
        </button>
        <span style={{ fontSize: 15, fontWeight: 600 }}>
          {karton.nev}
          {karton.rendszam ? ` · ${karton.rendszam}` : ''}
        </span>
        {!karton.aktiv && <span className="only">nem aktív</span>}
        <div className="stats">
          <div>
            Induló készlet<strong>{szam(karton.indulo_keszlet, 1)}</strong>
          </div>
          <div>
            Jelenlegi<strong>{szam(karton.jelenlegi_keszlet, 1)}</strong>
          </div>
          <div>
            Eltérés összesen
            <strong className={karton.osszes_elteres !== 0 ? 'bad2' : undefined}>
              {elteresJel(karton.osszes_elteres)}
            </strong>
          </div>
        </div>
      </div>

      {karton.elteres_napok > 0 && (
        <div className="uzenet flag">
          {karton.elteres_napok} napon volt eltérés a megszámolt és a számított záró közt,
          összesen {elteresJel(karton.osszes_elteres)} liter. A lánc minden nap a
          megszámolt értéken folytatódik, tehát ezek nem adódnak össze a készletben —
          de érdemes megnézni, honnan jönnek.
        </div>
      )}

      <div className="matrix">
        <table className="tt2">
          <thead>
            <tr>
              <th>Nap</th>
              <th className="n">Cím</th>
              <th className="n">Nyitó</th>
              <th className="n">Fel</th>
              <th className="n">Le</th>
              <th className="n">ebből átadás</th>
              <th className="n">Számított záró</th>
              <th className="n">Megszámolt</th>
              <th className="n">Eltérés</th>
              <th className="n">Egyenleg</th>
            </tr>
          </thead>
          <tbody>
            {karton.mozgasok.map((m) => (
              <tr key={m.datum}>
                <td style={{ padding: '6px 10px', fontWeight: 550, whiteSpace: 'nowrap' }}>
                  {m.felirat}
                  <small className="halvany"> · {m.statusz}</small>
                </td>
                <td className="calc2">{m.cimek}</td>
                <td className="calc2">{szam(m.nyito, 1)}</td>
                <td className="calc2">{m.fel ? szam(m.fel, 1) : '·'}</td>
                <td className="calc2">{m.le ? szam(m.le, 1) : '·'}</td>
                <td className="calc2">{m.atadas ? szam(m.atadas, 1) : '·'}</td>
                <td className="calc2">{szam(m.szamitott_zaro, 1)}</td>
                <td className="calc2">
                  {m.megszamolt_zaro === null ? (
                    <span className="halvany">—</span>
                  ) : (
                    szam(m.megszamolt_zaro, 1)
                  )}
                </td>
                <td className="calc2">
                  {m.elteres === null ? (
                    <span className="halvany">—</span>
                  ) : m.elteres === 0 ? (
                    <span style={{ color: 'var(--ok)' }}>✓</span>
                  ) : (
                    <span className="bad2">{elteresJel(m.elteres)}</span>
                  )}
                </td>
                <td className="calc2">
                  <b>{szam(m.egyenleg, 1)}</b>
                </td>
              </tr>
            ))}
            {karton.mozgasok.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Ehhez az autóhoz még nincs elszámolt nap.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
